// Story 8.3 (ADO-2012), D-1: the cart a basket call at a depot works on. A Shopper's cart is
// keyed by the depot and the Trading Account they order for there, so every call names both.
// The account is looked up for the depot the call works on, from one session snapshot:
//   - at the active depot, the active account;
//   - elsewhere, or at the active depot before an account is chosen, the account the depot's
//     Select Depot tile opens (Story 8.10, ADO-2019, DV-68): its entry's sole account
//     (`resolveChoice` with none named);
//   - otherwise none: before the choices have loaded, for staff who have not picked a store
//     account there, and at a Value Centre offering several accounts with none chosen (Story
//     8.4, ADO-2013, AC1: the Shopper must select one before they can order). A call without an
//     account is refused by the server rather than guessed.
//
// Enabler E8-G (ADO-3109), DA-8 (AC9): the cart is keyed by the range too, which the server reads
// from the kind of the entry the call goes through. At the active depot that is the active entry
// (so Chill and Fruit & Veg at depot 25 are the two carts they are), elsewhere the depot's
// primary entry. DA-10 (AC11): a staff user's active account is the store account it picked, and
// a staff user with none picked has no session cart at all (`null`), so no cart call is ever
// made for staff without an account; the browsing gate sends them to pick one.
//
// Story 8.4 (ADO-2013): the session's cart (`sessionCartTarget`, `useSessionCartTarget`) is the
// active depot plus that account and kind, the one identity the basket stores, the cut-off and
// the order-reference reads key on, so a change of account or of range is a change of cart.

import { useMemo } from 'react';
import { cartTargetKey, type CartTarget } from '../api/cart-target';
import type {
  OrderingAccount,
  OrderingEntry,
  OrderingEntryKind,
} from '../api/ordering-choices.client';
import { useSessionStore, type SessionContext } from '../auth/useSessionStore';
import { resolveSessionDepotId, useSessionDepotId } from '../availability/session-depot';
import { primaryEntryAtDepot, resolveChoice } from '../ordering/orderingChoices';

type CartSession = Pick<
  SessionContext,
  'activeDepot' | 'activeEntryKey' | 'activeTradingAccount' | 'orderingChoices'
>;

/** The cart at `depotId`: the depot, the account there (`null` when none is chosen) and the kind
 *  of the entry the cart is reached through (Enabler E8-G, ADO-3109, DA-8). */
export function cartTargetAt(
  depotId: string,
  session: CartSession = useSessionStore.getState(),
): CartTarget {
  const entry = entryAt(depotId, session);
  return {
    depotId,
    customerAccountCode: accountCodeAt(depotId, entry, session),
    entryKind: entry?.kind ?? 'DEPOT',
  };
}

/** The cart at `depotId` for a cart call (`cartTargetAt`), or `null` for a staff user with no
 *  store account picked there, who has no cart to call (DA-10). */
export function cartCallTargetAt(
  depotId: string,
  session: CartSession = useSessionStore.getState(),
): CartTarget | null {
  const cart = cartTargetAt(depotId, session);
  return isStaffWithoutAccount(session, cart.customerAccountCode) ? null : cart;
}

/** The session's cart: the valid active depot and the account there, or `null` with no
 *  valid active depot, and for a staff user with no store account picked (DA-10). */
export function sessionCartTarget(
  session: CartSession = useSessionStore.getState(),
): CartTarget | null {
  const depotId = resolveSessionDepotId(session.activeDepot);
  return depotId === null ? null : cartCallTargetAt(depotId, session);
}

/** The session's cart as one key (`cartTargetKey`), or `null` with no valid active depot: what
 *  the stores holding one cart's state compare to tell a change of cart. */
export function sessionCartKey(session: CartSession = useSessionStore.getState()): string | null {
  const cart = sessionCartTarget(session);
  return cart === null ? null : cartTargetKey(cart);
}

/** The session's cart as a SUBSCRIBED value: a new object only when the depot, the account or
 *  the entry kind changes, so an effect keyed on it re-runs exactly on a change of cart. */
export function useSessionCartTarget(): CartTarget | null {
  return useCartTargetAt(useSessionDepotId());
}

/** The cart at `depotId` (see `cartTargetAt`) as a SUBSCRIBED value, or `null` with no depot,
 *  and for a staff user with no store account picked there (DA-10). */
export function useCartTargetAt(depotId: string | null): CartTarget | null {
  const customerAccountCode = useSessionStore((session) =>
    depotId === null ? null : accountCodeAt(depotId, entryAt(depotId, session), session),
  );
  const staff = useSessionStore((session) => session.orderingChoices?.scope === 'DEPOT_IDS');
  const entryKind = useSessionStore(
    (session): OrderingEntryKind =>
      depotId === null ? 'DEPOT' : (entryAt(depotId, session)?.kind ?? 'DEPOT'),
  );
  return useMemo(
    () =>
      depotId === null || (staff && customerAccountCode === null)
        ? null
        : { depotId, customerAccountCode, entryKind },
    [depotId, customerAccountCode, entryKind, staff],
  );
}

/** Enabler E8-G (ADO-3109), DA-10: a staff user with no store account at the cart's depot, who
 *  has no cart there (a staff cart call without an account is refused). */
function isStaffWithoutAccount(session: CartSession, customerAccountCode: string | null): boolean {
  return session.orderingChoices?.scope === 'DEPOT_IDS' && customerAccountCode === null;
}

/** Enabler E8-G (ADO-3109): the range of the cart a call at `depotId` goes through, with its
 *  entry kind, or `null` when the entry names none (or the choices have not loaded). */
export interface CartRange {
  readonly assortmentCode: string;
  readonly entryKind: OrderingEntryKind;
}

export function cartRangeAt(
  depotId: string,
  session: CartSession = useSessionStore.getState(),
): CartRange | null {
  const entry = entryAt(depotId, session);
  return entry?.assortmentCode === undefined
    ? null
    : { assortmentCode: entry.assortmentCode, entryKind: entry.kind };
}

/** {@link cartRangeAt} for the session's depot as a SUBSCRIBED value: a new object only when the
 *  range or the entry kind changes (Chill and Fruit & Veg share depot 25's id). */
export function useSessionCartRange(): CartRange | null {
  const depotId = useSessionDepotId();
  const assortmentCode = useSessionStore((session) =>
    depotId === null ? null : (cartRangeAt(depotId, session)?.assortmentCode ?? null),
  );
  const entryKind = useSessionStore((session) =>
    depotId === null ? null : (cartRangeAt(depotId, session)?.entryKind ?? null),
  );
  return useMemo(
    () => (assortmentCode === null || entryKind === null ? null : { assortmentCode, entryKind }),
    [assortmentCode, entryKind],
  );
}

/** The entry a call at `depotId` goes through: the active entry at the active depot (Chill or
 *  Fruit & Veg at depot 25, as the Shopper chose), else the depot's primary entry (its `DEPOT`
 *  entry first). `null` before the choices load, or when no entry is at the depot. */
function entryAt(depotId: string, session: CartSession): OrderingEntry | null {
  const choices = session.orderingChoices;
  if (choices === null) return null;
  if (session.activeDepot === depotId) {
    const active = choices.entries.find((entry) => entry.key === session.activeEntryKey);
    if (active !== undefined && active.depotId === depotId) return active;
  }
  return primaryEntryAtDepot(choices.entries, depotId);
}

function accountCodeAt(
  depotId: string,
  entry: OrderingEntry | null,
  session: CartSession,
): string | null {
  return accountAt(depotId, entry, session)?.customerAccountCode ?? null;
}

/** The account a call at `depotId` names (see `accountCodeAt`), or `null` when there is none. */
function accountAt(
  depotId: string,
  entry: OrderingEntry | null,
  session: CartSession,
): OrderingAccount | null {
  if (session.activeDepot === depotId && session.activeTradingAccount !== null) {
    return session.activeTradingAccount;
  }
  const choices = session.orderingChoices;
  if (choices === null || choices.scope !== 'TRADING_ACCOUNTS' || entry === null) return null;
  return resolveChoice(choices, entry.key, null)?.account ?? null;
}

/**
 * Story 11.10 (ADO-1961), D-3: whether the store of the session's cart account (the account every
 * price request names, `usePriceAccountCode`) sees its retail margin, as a SUBSCRIBED value: the
 * account's `marginShown` from the ordering choices or the staff search. `false` with no depot or
 * no account chosen (staff before they pick one, OQ-1 (c)): the margin is never shown by default.
 */
export function useSessionMarginShown(): boolean {
  const depotId = useSessionDepotId();
  return useSessionStore(
    (session) =>
      depotId !== null &&
      accountAt(depotId, entryAt(depotId, session), session)?.marginShown === true,
  );
}
